import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline' | 'link';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-red text-white hover:bg-red-deep focus-visible:ring-red-soft active:bg-red-deep border-transparent',
  secondary:
    'bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 hover:bg-paper-warm dark:hover:bg-paper-dark-warm border-line dark:border-line-dark',
  ghost:
    'bg-transparent text-ink-1 dark:text-ink-dark-1 hover:bg-paper-warm dark:hover:bg-paper-dark-warm border-transparent',
  destructive:
    'bg-transparent text-red hover:bg-red-soft dark:hover:bg-red-soft-dark border-line dark:border-line-dark',
  outline:
    'bg-transparent text-ink-1 dark:text-ink-dark-1 hover:bg-paper-warm dark:hover:bg-paper-dark-warm border-line dark:border-line-dark',
  link:
    'bg-transparent text-red hover:text-red-deep border-transparent underline-offset-2 hover:underline px-0',
};

/**
 * 🔴 O PISO DE 44px, NO CELULAR (Frente 2, 12/09/2026).
 *
 * ⚠️ Medido no beta em 375px, com `elementFromPoint` (o único jeito de ver o
 * alvo de verdade): `sm` dava **37px**, `md` e `icon` davam **40px** — todos
 * abaixo do piso de 44 que o próprio sistema adota. O botão **"Parar tudo"**,
 * que é o freio de emergência do WhatsApp, era um deles.
 *
 * 🔴 A cura é altura de verdade e **só abaixo de `sm`**. Tentar crescer por
 * pseudo-elemento seria repetir o erro do `Tabs`, onde o alvo prometido era
 * recortado por um `overflow` do pai e ninguém percebeu por semanas — botão
 * mora em barra, rodapé de diálogo e célula de tabela, e qualquer um deles
 * pode recortar.
 *
 * ⚠️ **Isso muda a altura dos botões no telefone**, em todo o sistema: 44px
 * onde havia 32 ou 40. Foi decisão consciente desta rodada. **O desktop não
 * muda em nada** — `sm:` devolve os valores de sempre a partir de 640px.
 */
const SIZE_CLASSES: Record<Size, string> = {
  sm: 'h-11 sm:h-8 px-3 text-xs gap-1.5',
  md: 'h-11 sm:h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
  icon: 'h-11 w-11 sm:h-10 sm:w-10 p-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Ícone à esquerda do label */
  leftIcon?: ReactNode;
  /** Ícone à direita do label */
  rightIcon?: ReactNode;
  /** Faz o botão ocupar toda a largura disponível */
  fullWidth?: boolean;
}

/**
 * Botão padrão do design system IGM.
 *
 * Variants: primary (vermelho), secondary (papel), ghost, destructive, outline, link.
 * Sizes: sm, md (default), lg, icon (quadrado).
 *
 * Exemplos:
 *   <Button>Salvar</Button>
 *   <Button variant="ghost" size="sm">Cancelar</Button>
 *   <Button leftIcon={<PlusIcon />}>Novo cliente</Button>
 *   <Button loading>Enviando…</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // Base
          'inline-flex items-center justify-center rounded-xl border font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          // Variant
          VARIANT_CLASSES[variant],
          // Size (link ignora padding)
          variant !== 'link' && SIZE_CLASSES[size],
          variant === 'link' && 'h-auto p-0 text-sm',
          // Full width
          fullWidth && 'w-full',
          // Custom
          className
        )}
        {...props}
      >
        {loading ? (
          <Spinner />
        ) : (
          leftIcon && <span className="flex-shrink-0">{leftIcon}</span>
        )}
        <span>{children}</span>
        {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);
Button.displayName = 'Button';

function Spinner() {
  return (
    <svg
      className="animate-spin w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M4 12a8 8 0 0 1 8-8"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
